import { useEffect } from 'react';
import { Pill } from '../../../layout/Pill.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { PhoneInput } from '../components/PhoneInput.jsx';
import { ChildSelectModal } from '../components/ChildSelectModal.jsx';
import { useReceptionForm } from '../hooks/useReceptionForm.js';
import { useGuardianLookup } from '../hooks/useGuardianLookup.js';
import { formatDateTime } from '../../../shared/format.js';

// Backend enum: father|mother|brother|sister|grandfather|grandmother|other
// We display Arabic labels but always send the canonical English key.
const RELATIONS = [
  { key: 'father', label: 'أب' },
  { key: 'mother', label: 'أم' },
  { key: 'brother', label: 'أخ' },
  { key: 'sister', label: 'أخت' },
  { key: 'grandfather', label: 'جد' },
  { key: 'grandmother', label: 'جدة' },
  { key: 'other', label: 'آخر' }
];

export function Step1Guardian() {
  const guardian = useReceptionForm((s) => s.guardian);
  const updateGuardian = useReceptionForm((s) => s.updateGuardian);
  const applyGuardianLookup = useReceptionForm((s) => s.applyGuardianLookup);
  const existingChildren = useReceptionForm((s) => s.existingChildren);
  const setChildPickerOpen = useReceptionForm((s) => s.setChildPickerOpen);

  const lookup = useGuardianLookup(guardian.phone);

  useEffect(() => {
    if (lookup.found && lookup.found._id !== guardian.id) {
      applyGuardianLookup(lookup.found);
    } else if (lookup.settled && !lookup.found && !lookup.loading && guardian.id) {
      // settled يمنع مسح الزبون العائد (وأطفاله المستوردين) أثناء lookup لم يُحسم
      // بعد — كما يحدث لحظة العودة من خطوة 2 إلى خطوة 1.
      applyGuardianLookup(null);
    }
  }, [lookup.found, lookup.loading, lookup.settled]); // eslint-disable-line react-hooks/exhaustive-deps

  const phoneInvalid = guardian.phone.length > 0 && !/^07[0-9]{9}$/.test(guardian.phone);

  return (
    <div>
      <div className="card-h">
        <div className="flex-1">
          <h3 className="text-[14.5px] font-bold">بيانات ولي الأمر</h3>
          <div className="text-xs text-ink-3 mt-0.5">ابحث برقم الهاتف أو أنشئ سجلاً جديداً</div>
        </div>
      </div>
      <div className="card-b space-y-3">
        {guardian.isReturning ? (
          <div className="p-3 rounded-[10px] bg-orange-soft text-orange-deep">
            <div className="flex items-center gap-2">
              <Icon name="reception" size={16} />
              <div className="text-[12.5px]">
                <b>زبون عائد</b> — {guardian.name}
                {guardian.lastVisitAt ? ` — آخر زيارة ${formatDateTime(guardian.lastVisitAt)}` : ''}
              </div>
              <div className="ms-auto">
                <Pill variant="orange">عائد</Pill>
              </div>
            </div>
            {existingChildren.length > 0 ? (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-orange-deep/15">
                <div className="text-[12px] flex-1">
                  <b>الأطفال ({existingChildren.length}):</b>{' '}
                  {existingChildren
                    .map((c) => (c.hasActiveSession ? `${c.name} (بالداخل الآن)` : c.name))
                    .join('، ')}
                </div>
                {existingChildren.length > 1 ? (
                  <button
                    type="button"
                    className="btn"
                    style={{ padding: '4px 10px', fontSize: 11.5 }}
                    onClick={() => setChildPickerOpen(true)}
                  >
                    اختيار الأطفال
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">رقم الهاتف *</label>
            <PhoneInput
              value={guardian.phone}
              onChange={(phone) => updateGuardian({ phone })}
              error={phoneInvalid}
            />
            {phoneInvalid ? (
              <div className="text-[11px] text-red mt-1">يجب أن يبدأ بـ 07 ويتكوّن من 11 رقم</div>
            ) : null}
            {lookup.loading ? (
              <div className="text-[11px] text-ink-3 mt-1">جاري البحث...</div>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم الكامل *</label>
            <input
              className="input"
              value={guardian.name}
              onChange={(e) => updateGuardian({ name: e.target.value })}
              placeholder="مثال: حسن العامري"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">صلة القرابة</label>
            <div className="flex gap-1.5 flex-wrap">
              {RELATIONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={`btn ${guardian.relation === r.key ? 'btn-primary' : ''}`}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={() => updateGuardian({ relation: r.key })}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">رقم بديل</label>
            <PhoneInput
              value={guardian.secondaryPhone}
              onChange={(secondaryPhone) => updateGuardian({ secondaryPhone })}
              placeholder="اختياري"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
          <textarea
            className="input"
            rows={2}
            value={guardian.notes}
            onChange={(e) => updateGuardian({ notes: e.target.value })}
            placeholder="أي تنبيه مهم..."
          />
        </div>
      </div>

      <ChildSelectModal />
    </div>
  );
}
